import { csrf_headers, get_token, get_user_id } from '@/router/index.js';
import { PLAYGROUND_PLATFORM_KEYS } from '@/utils/playground_platform_launch';
import { show_page_loader, hide_page_loader } from '@/utils/page_loader';
import { PLATFORM_ENDPOINTS, PLATFORM_LABELS, PLATFORM_NAME_TO_KEY, PLAYGROUND_PLATFORM_ALIASES, SUPER_ADMIN_APP_KEYS } from './navbar_platform_constants';
import { build_platform_launch_request, safe_platform_launch_redirect_url } from './navbar_safe_url_helpers';

export const navbar_platform_methods = {
  get_platform_label(platform) {
    return Reflect.get(PLATFORM_LABELS, platform) || 'Platform';
  },

  normalize_platform_name(name) {
    return String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[._-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  },

  map_platform_names_to_app_keys(platform_names) {
    const keys = [];
    (platform_names || []).forEach((name) => {
      const normalized = this.normalize_platform_name(name);
      if (Reflect.has(PLATFORM_NAME_TO_KEY, normalized)) {
        const mapped_key = Reflect.get(PLATFORM_NAME_TO_KEY, normalized);
        if (typeof mapped_key === "string" && !keys.includes(mapped_key)) keys.push(mapped_key);
      }
    });
    return keys;
  },

  local_allowed_app_keys() {
    const role = String(localStorage.getItem("role") || "").toLowerCase();
    if (localStorage.getItem('is_playground_user') === '1') {
      return [...PLAYGROUND_PLATFORM_KEYS];
    }
    if (role.includes("super admin")) {
      return [...SUPER_ADMIN_APP_KEYS];
    }
    return null;
  },

  platform_access_user_id() {
    return this.user?.id || this.user?.user_id || localStorage.getItem('user_id');
  },

  async fetch_platform_access_payload(token, user_id) {
    const params = new URLSearchParams();
    params.set('user_id', String(user_id));
    const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/api/get_user_platform_access_api/?${params.toString()}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Token ${token}`,
      },
    });
    const payload = await res.json().catch(() => null);
    return res.ok && payload?.status === "success" ? payload : null;
  },

  enabled_platform_names(platform_access_payload) {
    return (platform_access_payload?.data || [])
      .filter(r => String(r.status || "").toUpperCase() === "ENABLED")
      .map(r => r.platform_name)
      .filter(Boolean);
  },

  async load_allowed_app_keys() {
    this.platform_access_loading = true;
    try {
      this.allowed_app_keys = await this.resolve_allowed_app_keys();
    } catch (e) {
      console.error("Failed to load platform access:", e);
      this.allowed_app_keys = [];
    } finally {
      this.platform_access_loading = false;
    }
  },

  async resolve_allowed_app_keys() {
    const local_keys = this.local_allowed_app_keys();
    if (local_keys) return local_keys;
    const token = get_token();
    const user_id = this.platform_access_user_id();
    if (!token || !user_id) return [];
    return this.fetch_allowed_app_keys(token, user_id);
  },

  async fetch_allowed_app_keys(token, user_id) {
    const payload = await this.fetch_platform_access_payload(token, user_id);
    if (!payload) return [];
    return this.map_platform_names_to_app_keys(this.enabled_platform_names(payload));
  },

  playground_alias_names(key) {
    return Reflect.get(PLAYGROUND_PLATFORM_ALIASES, key) ?? [key];
  },

  stored_playground_platforms() {
    let platforms;
    try {
      platforms = JSON.parse(localStorage.getItem('playground_platforms') ?? '[]');
    } catch {
      platforms = [];
    }
    return platforms;
  },

  normalise_platform_name(item) {
    return String(item?.name ?? '').trim().toLowerCase();
  },

  find_playground_platform(platforms, names) {
    return Array.isArray(platforms)
      ? platforms.find((item) => names.includes(this.normalise_platform_name(item)))
      : null;
  },

  playground_platform_id(key) {
    const names = this.playground_alias_names(key);
    const platforms = this.stored_playground_platforms();
    const platform = this.find_playground_platform(platforms, names);
    return platform?.id ?? null;
  },

  is_playground_user() {
    return localStorage.getItem('is_playground_user') === '1';
  },

  playground_session_context(key) {
    const context_id = localStorage.getItem('playground_context_id');
    const platform_id = this.playground_platform_id(key);
    return context_id && platform_id ? { context_id, platform_id } : null;
  },

  async request_playground_platform_session(context_id, platform_id) {
    const token = get_token();
    return fetch(
      `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/playground/prepare_playground_platform_session/`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Token ${token}` } : {}),
          ...csrf_headers(this.csrfToken),
        },
        body: JSON.stringify({ context_id, platform_id }),
      },
    );
  },

  handle_playground_prepare_response(response, body) {
    if (this.handle_expired_playground_session(response, body)) return false;
    if (this.handle_failed_playground_prepare(response, body)) return false;
    if (body.data?.redirect_url) {
      window.location = body.data.redirect_url;
      return false;
    }
    return true;
  },
  handle_expired_playground_session(response, body) {
    if (response.status !== 409 && body.status !== 'expired') return false;
    this.$notyf.error(body.message || 'Your Playground session has expired');
    this.$router.push({ name: 'Login' });
    return true;
  },
  handle_failed_playground_prepare(response, body) {
    if (response.ok && body.status === 'success') return false;

    this.$notyf.error(body.message || 'Unable to prepare this platform');
    return true;
  },
  async prepare_playground_platform_session(key) {
    if (!this.is_playground_user()) return true;

    const session_context = this.playground_session_context(key);
    if (!session_context) {
      this.$notyf.error('Playground context is not ready yet');
      return false;
    }
    try {
      const { context_id, platform_id } = session_context;
      const response = await this.request_playground_platform_session(context_id, platform_id);
      const body = await response.json();
      return this.handle_playground_prepare_response(response, body);
    } catch {
      this.$notyf.error('Unable to prepare this platform');
      return false;
    }
  },

  get_platform_endpoint(platform) {
    return Reflect.get(PLATFORM_ENDPOINTS, platform);
  },
  build_safe_api_url(platform) {
    const path = this.get_platform_endpoint(platform);
    if (!path) {
      throw new Error('Invalid platform');
    }
    return new URL(path, process.env.VUE_APP_API_BASE_URL).toString();
  },
  is_valid_platform_key(platform) {
      return /^[a-zA-Z0-9_-]+$/.test(platform);
  },
  get_redirect_url(data) {
      return data.redirect_url || data.data?.redirect_url;
  },
  platform_launch_url(platform) {
    return this.build_safe_api_url(platform);
  },

  async request_platform_launch(platform, token) {
    const launch_request = build_platform_launch_request({
      platform,
      token,
      csrfHeaders: csrf_headers(this.csrfToken),
      userId: get_user_id(),
    });
    return launch_request();
  },

  async get_launch_context(platform_key) {
    const token = get_token();
    if (!token) {
      this.$router.push({ name: 'Login' });
      return null;
    }
    const platform = await this.resolve_launch_platform(platform_key);
    if (!platform) return null;
    return ({ token, platform });
  },

  async resolve_launch_platform(platform_key) {
    const platform = String(platform_key || '').toLowerCase();
    if (!this.is_launch_platform_valid(platform)) return null;
    if (!await this.prepare_playground_platform_session(platform)) return null;
    return platform;
  },

  is_launch_platform_valid(platform) {
    if (!this.is_valid_platform_key(platform)) {
      this.$notyf.error('Unknown platform: invalid key');
      return false;
    }
    if (!this.get_platform_endpoint(platform)) {
      this.$notyf.error(`Unknown platform: ${platform}`);
      return false;
    }
    return true;
  },

  async launch_platform(platform_key) {
    if (this.launching_platform) return;
    const launch_context = await this.get_launch_context(platform_key);
    if (!launch_context) return;
    const { token, platform } = launch_context;
    this.launching_platform = true;
    show_page_loader(`Launching ${this.get_platform_label(platform)}...`, this.get_navbar_logo_src());
    try {
      const res = await this.request_platform_launch(platform, token);
      const data = await res.json();
      this.handle_platform_launch_response(res, data);
    } catch {
      this.$notyf.error('Failed to launch');
    } finally {
      this.launching_platform = false;
      hide_page_loader();
    }
  },
  is_platform_launch_success(res, data, redirect_url) {
    return res.ok && data.status === 'success' && Boolean(redirect_url);
  },
  platform_launch_error_message(data) {
    return data.message ?? 'Failed to launch platform';
  },
  handle_platform_launch_response(res, data) {
    const redirect_url = this.get_redirect_url(data);
    const safe_redirect_url = safe_platform_launch_redirect_url(redirect_url);
    if (res.ok && data.status === 'success' && safe_redirect_url) {
      window.location = safe_redirect_url;
      return;
    }
    this.notify_platform_launch_failure(data, redirect_url);
  },

  notify_platform_launch_failure(data, redirect_url) {
    if (redirect_url) {
      this.$notyf.error('Platform launch URL is not approved.');
      return;
    }
    this.$notyf.error(this.platform_launch_error_message(data));
  },
};